import { createSDKI18n } from '../../i18n'
import { XmaxSdkError } from '../../shared/sdk-error'
import {
  createImageFileStream,
  type ImageFileStreamOptions,
} from './image-file-stream'
import {
  normalizeHeicImageFile,
} from './heic-image'
import type { VideoFileStream } from './video-file-stream'

export {
  createImageFileStream,
  type ImageFileStreamOptions,
} from './image-file-stream'
export {
  isHeicImageFile,
  normalizeHeicImageFile,
  type NormalizeHeicImageOptions,
} from './heic-image'

export function isImageMediaFile(file: Blob | string): boolean {
  if (typeof file === 'string') {
    return /\.(png|jpe?g|webp|gif|bmp|heic|heif)$/i.test(file.split('?')[0])
  }
  if (file.type.startsWith('image/')) {
    return true
  }
  const name = 'name' in file && typeof file.name === 'string' ? file.name.toLowerCase() : ''
  return /\.(png|jpe?g|webp|gif|bmp|heic|heif)$/.test(name)
}

export function isVideoMediaFile(file: Blob | string): boolean {
  if (typeof file === 'string') {
    return /\.(mp4|mov|webm|m4v|avi|mkv)$/i.test(file.split('?')[0])
  }
  if (file.type.startsWith('video/')) {
    return true
  }
  const name = 'name' in file && typeof file.name === 'string' ? file.name.toLowerCase() : ''
  return /\.(mp4|mov|webm|m4v|avi|mkv)$/.test(name)
}

export async function createMediaFileStream(
  fileOrUrl: Blob | string,
  options: ImageFileStreamOptions = {},
): Promise<VideoFileStream> {
  const normalizedFileOrUrl = typeof fileOrUrl === 'string'
    ? fileOrUrl
    : (typeof File !== 'undefined' && fileOrUrl instanceof File
        ? await normalizeHeicImageFile(fileOrUrl)
        : fileOrUrl)

  if (isImageMediaFile(normalizedFileOrUrl)) {
    return createImageFileStream(normalizedFileOrUrl, options)
  }
  if (isVideoMediaFile(normalizedFileOrUrl)) {
    const { createVideoFileStream } = await import('./video-file-stream')
    return createVideoFileStream(normalizedFileOrUrl, options)
  }

  const i18n = options.i18n ?? createSDKI18n()
  const typeStr = typeof normalizedFileOrUrl === 'string'
    ? 'url'
    : (normalizedFileOrUrl.type || 'unknown')
  throw new XmaxSdkError(
    'UNSUPPORTED_MEDIA',
    i18n.t('errors.invalidMediaFileType', { type: typeStr }),
  )
}
